import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import { Business, Add, UnfoldMore, Save } from '@mui/icons-material';

import { Search, Clear } from '@mui/icons-material';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { centrosAPI, localidadesAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import { textMatch } from '../../../shared/utils/textUtils';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const PAGE_SIZE = 50;

const EMPTY = { centro: '', direccion: '', idLocalidad: '', telefono: '', latitud: '', longitud: '', capacidad: '' };

const GrillaCentros = () => {
  const [centros, setCentros] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [page, setPage] = useState(1);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const { sortedData, requestSort, getSortIcon } = useSortableTable(centros);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, lRes] = await Promise.all([centrosAPI.getCentros(), localidadesAPI.getLocalidades()]);
      setCentros(cRes.data || []);
      setLocalidades(lRes.data || []);
    } catch {
      setToast({ message: 'Error al cargar los datos.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = sortedData.filter(c =>
    textMatch(c.centro, buscar)
  );
  const totalPages = Math.ceil(filtrados.length / PAGE_SIZE);
  const paginados = filtrados.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const esDuplicado = () => {
    const val = form?.centro?.trim();
    if (!val) return false;
    return centros.some(c =>
      c.centro?.toLowerCase() === val.toLowerCase() &&
      (!editando || c.idCentro !== editando.idCentro)
    );
  };

  const handleNuevo = () => { setEditando(null); setForm(EMPTY); setMostrarForm(true); };
  const handleEditar = (c) => {
    setEditando(c);
    setForm({ centro: c.centro, direccion: c.direccion, idLocalidad: String(c.idLocalidad), telefono: c.telefono, latitud: c.latitud ?? '', longitud: c.longitud ?? '', capacidad: c.capacidad ?? '' });
    setMostrarForm(true);
  };
  const handleCancelar = () => { setMostrarForm(false); setEditando(null); setForm(EMPTY); };

  const handleGuardar = async (e) => {
    e.preventDefault();
    if (esDuplicado()) {
      setToast({ message: 'Ya existe un centro con ese nombre.', type: 'error' });
      return;
    }
    setLoading(true);
    try {
      const payload = { centro: form.centro.trim(), direccion: form.direccion.trim(), idLocalidad: Number(form.idLocalidad), telefono: form.telefono.trim(), latitud: form.latitud ? Number(form.latitud) : null, longitud: form.longitud ? Number(form.longitud) : null, capacidad: form.capacidad !== '' && form.capacidad != null ? Number(form.capacidad) : null };
      if (editando) {
        await centrosAPI.actualizar(editando.idCentro, payload);
        setToast({ message: 'Centro actualizado con éxito.', type: 'success' });
      } else {
        await centrosAPI.guardar(payload);
        setToast({ message: 'Centro guardado con éxito.', type: 'success' });
      }
      handleCancelar();
      cargar();
    } catch (err) {
      setToast({ message: err.response?.status === 409 ? 'Ya existe un centro con ese nombre.' : 'Error al guardar el centro.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmarEliminar = async () => {
    try {
      await centrosAPI.eliminar(confirmEliminar);
      setToast({ message: 'Centro eliminado con éxito.', type: 'success' });
      cargar();
    } catch {
      setToast({ message: 'Error al eliminar el centro.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  const duplicado = mostrarForm && esDuplicado();
  const capacidadInvalida = form.capacidad !== '' && !/^\d+$/.test(String(form.capacidad).trim());

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <Business sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Centros
          </h2>
          <span className="page-subtitle">Administración de centros</span>
        </div>

        <Modal show={mostrarForm} onHide={handleCancelar}>
          <Modal.Header closeButton>
            <Modal.Title>{editando ? 'Editar Centro' : 'Nuevo Centro'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>* Centro</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={100}
                  placeholder="Centro..."
                  value={form.centro}
                  onChange={(e) => setForm(f => ({ ...f, centro: e.target.value }))}
                  isInvalid={duplicado}
                  required
                  autoFocus
                />
                {duplicado && <Form.Control.Feedback type="invalid">Ya existe un centro con ese nombre.</Form.Control.Feedback>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>* Dirección</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={100}
                  placeholder="Dirección..."
                  value={form.direccion}
                  onChange={(e) => setForm(f => ({ ...f, direccion: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>* Localidad</Form.Label>
                <Form.Select
                  value={form.idLocalidad}
                  onChange={(e) => setForm(f => ({ ...f, idLocalidad: e.target.value }))}
                  required
                >
                  <option value="">Seleccione...</option>
                  {localidades.map(l => (
                    <option key={l.idLocalidad} value={l.idLocalidad}>{l.localidad}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>* Teléfono</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={30}
                  placeholder="Teléfono..."
                  value={form.telefono}
                  onChange={(e) => setForm(f => ({ ...f, telefono: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Latitud</Form.Label>
                <Form.Control
                  type="number"
                  step="0.000001"
                  placeholder="-34.603722"
                  value={form.latitud}
                  onChange={(e) => setForm(f => ({ ...f, latitud: e.target.value }))}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Longitud</Form.Label>
                <Form.Control
                  type="number"
                  step="0.000001"
                  placeholder="-58.381592"
                  value={form.longitud}
                  onChange={(e) => setForm(f => ({ ...f, longitud: e.target.value }))}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="centro-capacidad">Capacidad</Form.Label>
                <Form.Control
                  id="centro-capacidad"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Ej: 10"
                  value={form.capacidad}
                  onChange={(e) => setForm(f => ({ ...f, capacidad: e.target.value }))}
                  isInvalid={capacidadInvalida}
                />
                {capacidadInvalida && <Form.Control.Feedback type="invalid">La capacidad debe ser un número entero mayor o igual a 0.</Form.Control.Feedback>}
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelar} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || duplicado || capacidadInvalida || !form.centro.trim() || !form.direccion.trim() || !form.idLocalidad || !form.telefono.trim()}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {editando ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <div className="filters-card">
          <div className="filters-header">
            <h3 className="filters-title">Filtros de Búsqueda</h3>
          </div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${buscar ? 'filter-active' : ''}`}>
                <label className="filter-label">Centro</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Buscar centro..."
                  value={buscar}
                  onChange={(e) => { setBuscar(e.target.value); setPage(1); }}
                />
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={() => setPage(1)} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={() => { setBuscar(''); setPage(1); }}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left">
              <h3 className="results-title">Resultados</h3>
            </div>
            <div className="results-info">
              <span className="results-count">{filtrados.length} centros encontrados</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={handleNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo Centro
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('idCentro')} style={{ cursor: 'pointer' }}>
                    # {getSortIcon('idCentro') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('centro')} style={{ cursor: 'pointer' }}>
                    Centro {getSortIcon('centro') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('direccion')} style={{ cursor: 'pointer' }}>
                    Dirección {getSortIcon('direccion') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('telefono')} style={{ cursor: 'pointer' }}>
                    Teléfono {getSortIcon('telefono') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('localidad')} style={{ cursor: 'pointer' }}>
                    Localidad {getSortIcon('localidad') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('latitud')} style={{ cursor: 'pointer' }}>
                    Latitud {getSortIcon('latitud') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('longitud')} style={{ cursor: 'pointer' }}>
                    Longitud {getSortIcon('longitud') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('capacidad')} style={{ cursor: 'pointer' }}>
                    Capacidad {getSortIcon('capacidad') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 ? (
                  <tr><td colSpan="9" className="empty-state">{loading ? 'Cargando...' : 'No hay centros para mostrar'}</td></tr>
                ) : (
                  paginados.map((c) => (
                    <tr key={c.idCentro}>
                      <td>{c.idCentro}</td>
                      <td>{c.centro}</td>
                      <td>{c.direccion}</td>
                      <td>{c.telefono}</td>
                      <td>{c.localidad}</td>
                      <td>{c.latitud ?? '-'}</td>
                      <td>{c.longitud ?? '-'}</td>
                      <td>{c.capacidad ?? '—'}</td>
                      <td className="action-cell">
                        <RowActions onEdit={() => handleEditar(c)} onDelete={() => setConfirmEliminar(c.idCentro)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {page} de {totalPages}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar centro"
        message="¿Estás seguro que querés eliminar este centro?"
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaCentros;
